import { toast } from 'sonner';
import { create } from 'zustand';

import {
  setPasswordResetRequiredHandler,
  setPermissionDeniedHandler,
  setSessionExpiredHandler,
} from '@/lib/api-client';
import { tokenStorage } from '@/lib/token-storage';
import { authService, type LoginPayload } from '@/services/auth.service';
import type { AuthUser, RoleName } from '@/types/api';

interface AuthState {
  user: AuthUser | null;
  /** True selagi sesi tersimpan sedang dipulihkan saat aplikasi dibuka. */
  isInitializing: boolean;
  isAuthenticated: boolean;

  initialize: () => Promise<void>;
  login: (payload: LoginPayload) => Promise<AuthUser>;
  logout: () => Promise<void>;

  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasRole: (...roles: RoleName[]) => boolean;
  /** Fitur ini termasuk dalam paket langganan lembaga. */
  hasFeature: (feature: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isInitializing: true,
  isAuthenticated: false,

  /**
   * Memulihkan sesi saat aplikasi dibuka.
   *
   * Profil diambil ulang dari server, bukan dari cache lokal: peran dan
   * izin bisa saja berubah sejak login terakhir, dan menampilkan menu
   * berdasarkan izin basi akan membingungkan pengguna.
   */
  async initialize() {
    if (!tokenStorage.hasSession()) {
      set({ isInitializing: false, isAuthenticated: false, user: null });
      return;
    }

    try {
      const user = await authService.getProfile();
      set({ user, isAuthenticated: true, isInitializing: false });
    } catch {
      tokenStorage.clear();
      set({ user: null, isAuthenticated: false, isInitializing: false });
    }
  },

  async login(payload) {
    const result = await authService.login(payload);

    tokenStorage.setTokens(result.tokens);
    set({ user: result.user, isAuthenticated: true, isInitializing: false });

    return result.user;
  },

  async logout() {
    await authService.logout();
    set({ user: null, isAuthenticated: false });
  },

  hasPermission(permission) {
    const user = get().user;

    if (!user) {
      return false;
    }

    // Super Admin selalu lolos, sejalan dengan PermissionsGuard di backend.
    if (user.roles.includes('SUPER_ADMIN')) {
      return true;
    }

    return user.permissions.includes(permission);
  },

  hasAnyPermission(...permissions) {
    return permissions.some((item) => get().hasPermission(item));
  },

  hasRole(...roles) {
    const user = get().user;
    return user ? roles.some((role) => user.roles.includes(role)) : false;
  },

  hasFeature(feature) {
    const user = get().user;

    if (!user) {
      return false;
    }

    // Super Admin tidak berlangganan apa pun — termasuk saat menyamar untuk
    // menelusuri keluhan, di mana ia justru perlu melihat seluruh menu.
    // Sejalan dengan FeatureGuard di backend.
    if (user.roles.includes('SUPER_ADMIN')) {
      return true;
    }

    return user.features.includes(feature);
  },
}));

// Ketika refresh token gagal, store harus ikut tahu supaya router
// mengarahkan pengguna ke halaman masuk.
setSessionExpiredHandler(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false });
});

// Sandi baru saja direset Super Admin — token akses lama sempat masih
// sah, tapi backend menolaknya di luar auth/change-password & sejenis.
// Tandai di store supaya ProtectedRoute langsung mengarahkan ke halaman
// wajib ganti sandi tanpa menunggu pengguna me-refresh.
setPasswordResetRequiredHandler(() => {
  const user = useAuthStore.getState().user;
  if (user && !user.mustChangePassword) {
    useAuthStore.setState({ user: { ...user, mustChangePassword: true } });
  }
});

/**
 * Satu layar bisa menembakkan beberapa bacaan sekaligus, dan bila izinnya
 * kurang semuanya ditolak berbarengan. Tanpa penahan ini pemakainya diguyur
 * lima notifikasi berbunyi sama — yang justru membuat pesannya diabaikan.
 */
const recentDenials = new Set<string>();

setPermissionDeniedHandler((message) => {
  if (recentDenials.has(message)) {
    return;
  }

  recentDenials.add(message);
  setTimeout(() => recentDenials.delete(message), 5_000);

  toast.error(message, {
    description: 'Bagian ini tidak bisa ditampilkan. Hubungi admin lembaga bila Anda memerlukannya.',
  });
});
